// One checked file in the Legal upload box: what has been checked, the English
// vs Indonesian comparison, the filter tabs, and an overview of every finding,
// 5 a page, readable without opening anything. A line opens it in the side panel
// (ReviewPanel); while the panel is open the line being reviewed is highlighted
// here and the page follows Berikutnya.

import React, { useEffect, useRef } from 'react';
import { recheck, requestTranslation } from '../legalCheckStore';
import WORDS from './words';
import {
  LEVELS, atOf, findingsOf, isOld, matches, ordered, placeOf, titleOf,
} from './reviewModel';
import {
  forget, noteKeys, openPanel, pickFilter, setBoxPage, setLang, toast, useReview, view,
} from './reviewStore';
import {
  AiMark, CmpCallout, DOCX_ICON, NewTag, SevChip, Steps, Tabs, cmpState, useElapsed,
} from './parts';
import './review.css';

const PER_PAGE = 5;
const READING = ['pending', 'queued', 'running'];
const extOf = (name) => (String(name || '').split('.').pop() || '').toUpperCase().slice(0, 4);

function LangSwitch({ st, w }) {
  return (
    <div className="lr-lang" role="group" aria-label={w.langPick} title={w.langPick}>
      {['id', 'en'].map((l) => (
        <button key={l} type="button" aria-pressed={st.lang === l} onClick={() => setLang(l)}>{l.toUpperCase()}</button>
      ))}
    </div>
  );
}

function Working({ name, title, sub }) {
  const sec = useElapsed(true);
  return (
    <div className="lr-box" role="status" aria-live="polite">
      <div className="lr-file">{DOCX_ICON(extOf(name))}
        <div><div className="lr-fname">{name}</div>
          <div className="lr-steps"><span className="lr-st run"><span className="lr-spin" aria-hidden="true" />{title}{sec >= 3 ? ` (${sec}s)` : ''}</span></div>
          <div className="lr-fmeta">{sub}</div>
        </div>
      </div>
    </div>
  );
}

function ReviewCard({ source, entry }) {
  const st = useReview();
  const w = WORDS[st.lang];
  const { state, data, name, error } = entry;
  const v = view(entry.uid);
  const prev = useRef({ ai: null, tr: null });

  const ready = state === 'ready' && data && data.rules && data.rules.status === 'done';
  const all = ready ? findingsOf(data) : [];
  const keys = all.map((f) => f.key).join(',');

  // New findings (the AI read, the comparison) are marked until opened, and a
  // short message says how many arrived.
  useEffect(() => {
    if (!ready) return;
    const added = noteKeys(entry.uid, all.map((f) => f.key));
    const ai = data.ai.status;
    const tr = data.translation.status;
    if (prev.current.tr && prev.current.tr !== 'done' && tr === 'done') toast(w.cmpToast(added));
    else if (prev.current.ai && prev.current.ai !== 'done' && ai === 'done') toast(w.aiToast(added));
    prev.current = { ai, tr };
  }, [ready, keys, data && data.ai && data.ai.status, data && data.translation && data.translation.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const layout = ready ? data.layout : null;
  const boxList = ready ? ordered(all.filter((f) => !f.minor && matches(f, v.level, v.source)), layout) : [];
  const pages = Math.max(1, Math.ceil(boxList.length / PER_PAGE));
  const page = Math.min(Math.max(1, v.boxPage), pages);
  const panelHere = st.panel.open && st.panel.uid === entry.uid;

  // The box keeps its page on the finding being reviewed.
  useEffect(() => {
    if (!panelHere || !v.active) return;
    const i = boxList.findIndex((f) => f.key === v.active);
    if (i >= 0) {
      const want = Math.floor(i / PER_PAGE) + 1;
      if (want !== v.boxPage) setBoxPage(entry.uid, want);
    }
  }, [panelHere, v.active]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state === 'skipped') {
    return <div className="lr-box plain"><div className="lr-fname">{name}</div><p className="lr-muted">{w.notChecked}</p></div>;
  }
  const longRead = state === 'ready' && data && READING.includes(data.rules.status);
  if (state === 'checking' || longRead) {
    return (
      <Working name={name} title={longRead ? w.readingLong : w.reading}
               sub={longRead ? w.readingLongSub : /\.pdf$/i.test(name) ? w.readingScan : /\.doc$/i.test(name) ? w.readingDoc : w.readingSub} />
    );
  }
  if (!ready) {
    const failed = data && data.rules && data.rules.status === 'failed';
    // The server has no Word, so an old .doc can never be read there: say what to
    // do about it rather than blame the file, and offer no Recheck that would
    // only fail the same way. Shown only when the check actually failed, so a
    // server that can read .doc never shows it.
    const docOnly = failed && /\.doc$/i.test(name);
    const why = docOnly ? w.docFailed
      : failed ? w.failed(data.rules.reason)
      : w.unavailable(error && error !== 'Checker unavailable' ? error : '');
    return (
      <div className="lr-box plain">
        <div className="lr-fc-row">
          <div className="lr-file"><div className="lr-fname">{name}</div></div>
          {!docOnly && <button type="button" className="lr-btn quiet" onClick={() => recheck(source, entry.uid)}>{w.recheck}</button>}
        </div>
        <p className="lr-muted">{why}</p>
      </div>
    );
  }

  const slice = boxList.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const caveats = (data.rules.caveats_i18n || (data.rules.caveats || []).map((c) => ({ en: c, id: c })))
    .map((c) => c[st.lang] || c.en);
  const firstFresh = ordered(all, layout).find((f) => v.fresh.has(f.key));
  const startCompare = () => requestTranslation(source, entry.uid);
  const meta = data.reused ? w.reused : data.rules.ms != null ? w.machineMs(Math.max(0.1, data.rules.ms / 1000).toFixed(1)) : '';

  return (
    <div className="lr-box">
      <div className="lr-fc-row">
        <div className="lr-file">
          {DOCX_ICON(extOf(name))}
          <div>
            <div className="lr-fname">{name}</div>
            <Steps data={data} w={w} freshCount={v.fresh.size}
                   onSee={() => firstFresh && openPanel(source, entry.uid, firstFresh.key)} />
          </div>
        </div>
        <div className="lr-act">
          <LangSwitch st={st} w={w} />
          <button type="button" className="lr-btn quiet" title={meta}
                  onClick={() => { forget(entry.uid); recheck(source, entry.uid); }}>{w.recheck}</button>
          <button type="button" className="lr-btn primary" onClick={() => openPanel(source, entry.uid, null)}>{w.open}</button>
        </div>
      </div>
      {cmpState(data) !== 'none' && <CmpCallout data={data} w={w} onStart={startCompare} />}
      {data.rules.paragraphs === 0 && <p className="lr-warn">{w.nothingRead}</p>}
      {caveats.length > 0 && <p className="lr-muted" style={{ fontSize: 12 }}>{w.note}: {caveats.join('; ')}</p>}
      {isOld(data) && <p className="lr-muted" style={{ fontSize: 12 }}>{w.oldResult}</p>}
      <Tabs findings={all} level={v.level} source={v.source} w={w}
            onPick={(k) => pickFilter(entry.uid, k, LEVELS)} />
      <div className="lr-ov">
        {slice.length === 0 && <p className="lr-empty">{all.length ? w.empty : w.noFindings}</p>}
        {slice.map((f) => (
          <button key={f.key} type="button"
                  className={`lr-ov-line${panelHere && v.active === f.key ? ' on' : ''}${f.challenged ? ' gone' : ''}`}
                  onClick={() => openPanel(source, entry.uid, f.key)}>
            <SevChip sev={f.sev} />
            <AiMark f={f} w={w} />
            <span className="lr-ov-loc">{placeOf(f, st.lang, w)}<NewTag on={v.fresh.has(f.key)} w={w} /></span>
            <span className="lr-ov-t" title={atOf(f, st.lang, w)}>{titleOf(f, st.lang)}</span>
          </button>
        ))}
        <div className="lr-ov-pager">
          <span className="lr-legend">{w.legend}</span>
          <span>{w.page(page, pages)}</span>
          <button className="lr-pg-btn" type="button" aria-label={w.prevPage} disabled={page <= 1}
                  onClick={() => setBoxPage(entry.uid, page - 1)}>‹</button>
          <button className="lr-pg-btn" type="button" aria-label={w.nextPage} disabled={page >= pages}
                  onClick={() => setBoxPage(entry.uid, page + 1)}>›</button>
        </div>
      </div>
    </div>
  );
}

export default ReviewCard;
